import { useQuery } from '@tanstack/react-query';

import { CLASS_ENTITY_TYPE } from 'constants/logic';
import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';

import {
  adaptWeeklyEarningsResponse,
  CHART_METRIC,
  createComingSoonWeeklyEarningsData,
  isVenueNotOnSmartRateError,
  sortClassesByName,
} from './WeeklyEarnings.helpers';

// Both metrics return the same `GetSmartRateInsightsResponse` shape, so they
// share the adapter and differ only by which insights endpoint is called.
const INSIGHTS_URL_BY_METRIC = {
  [CHART_METRIC.TOTAL_EARNINGS]: URLS.smartRate.totalEarnings,
  [CHART_METRIC.AVERAGE_PAYOUT_RATE]: URLS.smartRate.averagePayoutRate,
};

export const weeklyEarningsKeys = {
  all: ['weeklyEarnings'],
  insights: (metric, venueId, classIds = []) => [...weeklyEarningsKeys.all, 'insights', metric, venueId, classIds],
  venueClasses: venueId => [...weeklyEarningsKeys.all, 'venueClasses', venueId],
};

export async function fetchWeeklyEarnings({ venueId, classIds = [], metric = CHART_METRIC.TOTAL_EARNINGS }) {
  const buildUrl = INSIGHTS_URL_BY_METRIC[metric] || URLS.smartRate.totalEarnings;
  try {
    const response = await httpClient.get(buildUrl(venueId, classIds), { ignoreInternalError: true });
    const adapted = adaptWeeklyEarningsResponse(response?.data || {});
    // Only override when 200 OK returned an empty heatmap and no semantic flag
    // explains why — the selected class has no bookable data yet. We derive
    // "empty heatmap" from the adapted rows (single source of truth) rather than
    // re-traversing the raw payload.
    // `mustSelectClassType` is a venue property that may remain true even after a
    // class is picked, so treat an empty result as "no class data" whenever the
    // caller already supplied a classId (classIds.length > 0).
    if (
      adapted.rows.length === 0 &&
      adapted.hasQualifyingReservationCount &&
      adapted.hasMoreThan8WeeksData &&
      (!adapted.mustSelectClassType || classIds.length > 0)
    ) {
      return { ...adapted, hasQualifyingReservationCount: false };
    }
    return adapted;
  } catch (error) {
    if (isVenueNotOnSmartRateError(error)) {
      return createComingSoonWeeklyEarningsData();
    }
    reportError(error);
    throw error;
  }
}

export async function fetchVenueClasses(venueId) {
  try {
    const response = await httpClient.get(URLS.venue.classes(venueId), {
      params: { entity_type: CLASS_ENTITY_TYPE.CLASS, only_active: true },
      ignoreInternalError: true,
    });
    const classes = transformKeysToCamelCase(response.data?.data) || [];
    // The class-type filter lists classes alphabetically, matching Class Insights.
    return sortClassesByName(classes);
  } catch (error) {
    reportError(error);
    throw error;
  }
}

export function useWeeklyEarningsQuery({ venueId, classIds = [], metric = CHART_METRIC.TOTAL_EARNINGS }, options = {}) {
  return useQuery({
    queryKey: weeklyEarningsKeys.insights(metric, venueId, classIds),
    queryFn: () => fetchWeeklyEarnings({ venueId, classIds, metric }),
    enabled: !!venueId,
    ...options,
  });
}

export function useVenueClassesQuery(venueId, options = {}) {
  return useQuery({
    queryKey: weeklyEarningsKeys.venueClasses(venueId),
    queryFn: () => fetchVenueClasses(venueId),
    enabled: !!venueId,
    ...options,
  });
}
